import { DateTime, Interval } from 'luxon'
import Entry from './Entry'
import Image from './Image'

export const TRANSPORT_TYPES = {
  DEFAULT: null,
  PLANE: 'plane',
  FERRY: 'ferry',
  BIKE: 'bike',
  FOOT: 'foot',
  CAR: 'car',
}

function addEnglishOrdinalToNumber(n) {
  const num = Number.parseInt(n)
  if (Number.isNaN(num)) return n
  const s = ["th", "st", "nd", "rd"];
  const v = num%100;
  return num + (s[(v-20)%10] || s[v] || s[0]);
}

function constructWhenString(instant, start, end, interval) {
  if (!(instant || start || end || interval)) return ''
  let formattedDate
  if (interval) {
    if (start.hasSame(end, 'day')) {
      formattedDate = start.toFormat('d MMMM')
    } else {
      formattedDate = interval.toFormat('d MMMM')
    }
  } else {
    formattedDate = (instant || start || end).toFormat('d MMMM')
  }
  return formattedDate.split(' ')
    .map(addEnglishOrdinalToNumber)
    .join(' ')
}

function dateIsMissingOrSame (d1, d2) {
  return !d1 || !d1.isValid || d1.equals(d2)
}

function dateIsPresentAndDifferent (d1, d2) {
  return d1 && d1.isValid && !d1.equals(d2)
}

export default class Waypoint {
  constructor ({ id, lonlat, when, altitude, temperature, entries, onwardTransport }) {
    this.id = id
    this.title = ''
    this.images = []
    this.lonlat = lonlat
    this.lat = lonlat[1]
    this.lng = lonlat[0]
    this.latlng = [this.lat, this.lng]
    this.altitude = altitude
    this.temperature = temperature
    this.onwardTransport = onwardTransport

    this.timezone = when.timezone || 'Etc/UTC'

    const fromIsoOptions = { zone: this.timezone }
    if (when.instant) this.instant = DateTime.fromISO(when.instant, fromIsoOptions)
    if (when.start) this.start = DateTime.fromISO(when.start, fromIsoOptions)
    if (when.end) this.end = DateTime.fromISO(when.end, fromIsoOptions)
    if (when.start && when.end) {
      this.interval = Interval.fromDateTimes(this.start, this.end)
    }
    this.whenString = constructWhenString(this.instant, this.start, this.end, this.interval)

    this.entries = entries || []
    this.concurrentEntries = this.entries.filter(
      entry => dateIsMissingOrSame(entry.date, this.instant || this.start)
    )
    this.separateEntries = this.entries.filter(
      entry => dateIsPresentAndDifferent(entry.date, this.instant || this.start)
    )
    this.entries.forEach(entry => entry.attachWaypoint(this))
  }

  isStop () {
    return this instanceof StopWaypoint
  }

  isReading () {
    return this instanceof ReadingWaypoint
  }

  hasImages() {
    return this.images.length > 0;
  }

  hasEntries() {
    return this.entries.length > 0;
  }

  hasStats() {
    return this.temperature != null || this.altitude != null;
  }

  hasContent() {
    return this.title || this.hasImages() || this.hasEntries() // || this.hasStats()
  }

  hasWhen() {
    return this.whenString.length > 0
  }

  static fromJson(json) {
    const waypointArgs = {
      ...json,
      entries: json.entries ? json.entries.map(entry => Entry.fromJson(entry)) : [],
      images: json.images ? json.images.map(image => Image.fromJson(image)) : []
    }

    const WaypointClass = json.type === 'stop' ? StopWaypoint : ReadingWaypoint
    return new WaypointClass(waypointArgs)
  }
}

export class StopWaypoint extends Waypoint {
  constructor(args) {
    super(args)
    this.title = args.title
    this.images = args.images || []
  }
}

export class ReadingWaypoint extends Waypoint {
  constructor(args) {
    super(args)
  }
}
